import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Divider,
  Link,
  Stack,
  Typography,
} from '@mui/material';

/**
 * Mentions légales — content lives in i18n under `legal.legalNotice.*`
 * and is fully translated to fr/en/de.
 */
export function LegalNoticePage() {
  const { t, i18n } = useTranslation();
  useEffect(() => {
    document.title = t('legal.legalNotice.documentTitle');
  }, [t, i18n.language]);

  return (
    <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ letterSpacing: '0.12em', fontWeight: 700 }}
      >
        {t('legal.legalNotice.overline')}
      </Typography>
      <Typography variant="h3" sx={{ mt: 1, mb: 4 }}>
        {t('legal.legalNotice.title')}
      </Typography>

      <Box sx={{ '& p, & li': { lineHeight: 1.8, color: 'text.primary' }, '& h2': { mt: 4 } }}>
        <Section title={t('legal.legalNotice.s1.title')}>
          <Typography>{t('legal.legalNotice.s1.p1')}</Typography>
          <Box component="ul" sx={{ pl: 3, my: 1 }}>
            <li>
              <strong>{t('legal.legalNotice.s1.ownerLabel')}</strong>{' '}
              {t('legal.legalNotice.s1.ownerValue')}
            </li>
            <li>
              <strong>{t('legal.legalNotice.s1.statusLabel')}</strong>{' '}
              {t('legal.legalNotice.s1.statusValue')}
            </li>
            <li>
              <strong>{t('legal.legalNotice.s1.addressLabel')}</strong>{' '}
              {t('legal.contact.city')}
            </li>
            <li>
              <strong>{t('legal.legalNotice.s1.emailLabel')}</strong>{' '}
              <Mail to="info@winorgroup.com" />
            </li>
            <li>
              <strong>{t('legal.legalNotice.s1.publisherLabel')}</strong>{' '}
              {t('legal.legalNotice.s1.publisherValue')}
            </li>
            <li>
              <strong>{t('legal.legalNotice.s1.webmasterLabel')}</strong>{' '}
              {t('legal.legalNotice.s1.webmasterValue')}
            </li>
          </Box>
          <Typography component="div">
            <strong>{t('legal.legalNotice.s1.hostLabel')}</strong>
            <Box component="div" sx={{ mt: 0.5 }}>
              {t('legal.legalNotice.s1.hostName')}
              <br />
              {t('legal.legalNotice.s1.hostStreet')}
              <br />
              {t('legal.legalNotice.s1.hostCity')}
              <br />
              {t('legal.legalNotice.s1.hostPhone')}
            </Box>
          </Typography>
        </Section>

        <Section title={t('legal.legalNotice.s2.title')}>
          <Box component="ul" sx={{ pl: 3, my: 1 }}>
            <li>
              <strong>{t('legal.legalNotice.s2.clientLabel')}</strong>{' '}
              {t('legal.legalNotice.s2.clientValue')}
            </li>
            <li>
              <strong>{t('legal.legalNotice.s2.userLabel')}</strong>{' '}
              {t('legal.legalNotice.s2.userValue')}
            </li>
            <li>
              <strong>{t('legal.legalNotice.s2.contentLabel')}</strong>{' '}
              {t('legal.legalNotice.s2.contentValue')}
            </li>
            <li>
              <strong>{t('legal.legalNotice.s2.personalDataLabel')}</strong>{' '}
              {t('legal.legalNotice.s2.personalDataValue')}
            </li>
          </Box>
        </Section>

        <Section title={t('legal.legalNotice.s3.title')}>
          <Typography>{t('legal.legalNotice.s3.p1')}</Typography>
          <Typography sx={{ mt: 1.5 }}>{t('legal.legalNotice.s3.p2')}</Typography>
        </Section>

        <Section title={t('legal.legalNotice.s4.title')}>
          <BulletsKey k="legal.legalNotice.s4.bullets" />
        </Section>

        <Section title={t('legal.legalNotice.s5.title')}>
          <Typography>{t('legal.legalNotice.s5.p1')}</Typography>
          <Typography sx={{ mt: 1.5 }}>{t('legal.legalNotice.s5.p2')}</Typography>
        </Section>

        <Section title={t('legal.legalNotice.s6.title')}>
          <Typography>{t('legal.legalNotice.s6.p1')}</Typography>
          <BulletsKey k="legal.legalNotice.s6.bullets" />
        </Section>

        <Section title={t('legal.legalNotice.s7.title')}>
          <Typography>{t('legal.legalNotice.s7.p1')}</Typography>

          <SubHeading>{t('legal.legalNotice.s7.sub1.title')}</SubHeading>
          <Typography>{t('legal.legalNotice.s7.sub1.p1')}</Typography>
          <BulletsKey k="legal.legalNotice.s7.sub1.bullets" />

          <SubHeading>{t('legal.legalNotice.s7.sub2.title')}</SubHeading>
          <Typography>{t('legal.legalNotice.s7.sub2.p1')}</Typography>
          <BulletsKey k="legal.legalNotice.s7.sub2.bullets" />
          <Typography>{t('legal.legalNotice.s7.sub2.p2')}</Typography>

          <SubHeading>{t('legal.legalNotice.s7.sub3.title')}</SubHeading>
          <Typography>{t('legal.legalNotice.s7.sub3.p1')}</Typography>
          <BulletsKey k="legal.legalNotice.s7.sub3.bullets" />
          <Typography>
            {t('legal.legalNotice.s7.sub3.p2Before')}
            <Mail to="info@winorgroup.com" />
          </Typography>
        </Section>

        <Section title={t('legal.legalNotice.s8.title')}>
          <Typography>{t('legal.legalNotice.s8.p1')}</Typography>
        </Section>

        <Section title={t('legal.legalNotice.s9.title')}>
          <Typography>{t('legal.legalNotice.s9.p1')}</Typography>
          <Typography sx={{ mt: 1.5 }}>{t('legal.legalNotice.s9.p2')}</Typography>
        </Section>

        <Section title={t('legal.legalNotice.s10.title')}>
          <Typography>{t('legal.legalNotice.s10.p1')}</Typography>
        </Section>

        <Section title={t('legal.legalNotice.s11.title')}>
          <Typography>{t('legal.legalNotice.s11.p1')}</Typography>
          <Typography sx={{ mt: 1.5 }}>{t('legal.legalNotice.s11.p2')}</Typography>
        </Section>
      </Box>
    </Container>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <>
      <Divider sx={{ my: 4 }} />
      <Typography variant="h5" component="h2" sx={{ mb: 2, fontWeight: 700 }}>
        {title}
      </Typography>
      <Stack spacing={1}>{children}</Stack>
    </>
  );
}

function SubHeading({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      variant="subtitle1"
      component="h3"
      sx={{ mt: 2, mb: 1, fontWeight: 700 }}
    >
      {children}
    </Typography>
  );
}

function BulletsKey({ k }: { k: string }) {
  const { t } = useTranslation();
  const items = t(k, { returnObjects: true }) as string[];
  return (
    <Box component="ul" sx={{ pl: 3, my: 1 }}>
      {items.map((item, i) => (
        <li key={i}>{item}</li>
      ))}
    </Box>
  );
}

function Mail({ to }: { to: string }) {
  return (
    <Link href={`mailto:${to}`} sx={{ color: 'primary.main', fontWeight: 500 }}>
      {to}
    </Link>
  );
}
